import {BlockedIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {getGroupCreatedAt} from '../utils/get-group-created-at'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {BlockedByAddedEvent$key} from './__generated__/BlockedByAddedEvent.graphql'
import styles from './DependencyEvent.module.css'
import {IssueLink} from './IssueLink'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'

export type BaseDependencyEventProps = {
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  repositoryId: string
  ownerLogin: string
}

type EventItem = BlockedByAddedEvent$key & {createdAt?: string}

type BlockedByAddedEventProps = BaseDependencyEventProps & {
  queryRef: EventItem
  rollupGroup?: Record<'BlockedByAddedEvent', EventItem[]>
}

export const BlockedByAddedEventFragment = graphql`
  fragment BlockedByAddedEvent on BlockedByAddedEvent {
    databaseId
    actor {
      ...TimelineRowEventActor
    }
    createdAt
    blockingIssue {
      ...IssueLink
    }
  }
`

export function BlockedByAddedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  repositoryId,
  ownerLogin,
}: BlockedByAddedEventProps) {
  const {actor, createdAt, blockingIssue, databaseId} = useFragment(BlockedByAddedEventFragment, queryRef)

  if (!blockingIssue) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup?.BlockedByAddedEvent || []
  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup
  const eventCreatedAt = getGroupCreatedAt(queryRef.createdAt, rolledUpGroup)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      showAgoTimestamp={false}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={BlockedIcon}
    >
      <TimelineRow.Main>
        <span>
          {itemsToRender.length === 1
            ? LABELS.timeline.blockedByAdded.single
            : LABELS.timeline.blockedByAdded.multiple(itemsToRender.length)}
        </span>{' '}
        {eventCreatedAt ? (
          <Ago timestamp={new Date(eventCreatedAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        ) : null}
      </TimelineRow.Main>
      <TimelineRow.Secondary>
        <ul className={styles.issueDependenciesList}>
          {itemsToRender.map((item, index) => (
            <BlockedByAddedEventItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${databaseId}_${index}`}
              event={item}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />
          ))}
        </ul>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function BlockedByAddedEventItem({
  event,
  targetRepositoryId,
  targetOwnerLogin,
}: {
  event: BlockedByAddedEvent$key
  targetRepositoryId: string
  targetOwnerLogin: string
}) {
  const {blockingIssue} = useFragment(BlockedByAddedEventFragment, event)

  if (!blockingIssue) {
    return null
  }

  return (
    <li className={styles.issueDependencyItem}>
      <IssueLink data={blockingIssue} targetRepositoryId={targetRepositoryId} targetOwnerLogin={targetOwnerLogin} />
    </li>
  )
}

try{ BlockedByAddedEvent.displayName ||= 'BlockedByAddedEvent' } catch {}
try{ BlockedByAddedEventItem.displayName ||= 'BlockedByAddedEventItem' } catch {}